import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Avatar,
  LinearProgress,
  IconButton,
  Tooltip,
  Paper,
  Divider,
  useTheme,
  alpha
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  TrendingUp,
  TrendingDown,
  Assignment,
  BugReport,
  Task,
  Group,
  Schedule,
  Add,
  Visibility,
  Edit,
  Delete,
  MoreVert,
  Star,
  StarBorder,
  CheckCircle,
  Warning,
  Error,
  Info
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';
import { Project, Issue, User, UserRole } from '../types';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import OrganizationSelector from '../components/Common/OrganizationSelector';
import VelocityDashboard from '../components/Sprint/VelocityDashboard';

interface DashboardStats {
  totalProjects: number;
  activeProjects: number;
  totalIssues: number;
  completedIssues: number;
  totalUsers: number;
  activeUsers: number;
}

const Dashboard: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user, selectedOrganizationId } = useAuth();
  const isEmployee = user?.role === UserRole.EMPLOYEE;
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<DashboardStats>({
    totalProjects: 0,
    activeProjects: 0,
    totalIssues: 0,
    completedIssues: 0,
    totalUsers: 0,
    activeUsers: 0
  });
  const [recentProjects, setRecentProjects] = useState<Project[]>([]);
  const [recentIssues, setRecentIssues] = useState<Issue[]>([]);
  const [topUsers, setTopUsers] = useState<User[]>([]);

  useEffect(() => {
    console.log('📊 Dashboard: selectedOrganizationId changed to:', selectedOrganizationId);
    loadDashboardData();
  }, [selectedOrganizationId]);

  const loadDashboardData = useCallback(async () => {
    try {
      console.log('📊 Dashboard: Loading data for organization:', selectedOrganizationId);
      setLoading(true);
      
      // Use getUserProjects for regular users, getProjects for admin users
      const isAdmin = user?.role && [UserRole.ADMIN, UserRole.SUPER_MANAGER].includes(user.role);
      const isRootUser = user?.role === UserRole.ROOT_USER;
      
      // ROOT_USER should be redirected to root admin dashboard
      if (isRootUser) {
        console.warn('🔒 ROOT_USER accessing organization dashboard - redirecting to root admin dashboard');
        navigate('/root-admin-dashboard', { replace: true });
        return;
      }
      
      let projects: any[] = [];
      if (isAdmin) {
        projects = await apiService.getProjects();
      } else {
        projects = await apiService.getUserProjects();
      }
      
      let users: any[] = [];
      // Regular users get all users from their org
      users = await apiService.getAllUsers();

      // Ensure we have arrays, not undefined
      if (!Array.isArray(projects)) {
        console.warn('Projects is not an array:', projects);
        projects = [];
      }
      if (!Array.isArray(users)) {
        console.warn('Users is not an array:', users);
        users = [];
      }

      const activeProjects = projects.filter((p: any) => p.isActive).length;

      setStats({
        totalProjects: projects.length,
        activeProjects,
        totalIssues: 0, // Temporarily set to 0
        completedIssues: 0, // Temporarily set to 0
        totalUsers: users.length,
        activeUsers: users.filter(u => u.isActive).length
      });

      setRecentProjects(projects.slice(0, 5));
      setRecentIssues([]); // Temporarily set to empty array
      setTopUsers(users.slice(0, 5));
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedOrganizationId, user?.role]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'success';
      case 'completed': return 'info';
      case 'on-hold': return 'warning';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  const getIssueTypeIcon = (type: string) => {
    switch (type) {
      case 'story': return <Assignment />;
      case 'bug': return <BugReport />;
      case 'task': return <Task />;
      default: return <Assignment />;
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <Box sx={{ 
      minHeight: '100vh',
      background: theme.palette.mode === 'dark' 
        ? 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)'
        : 'linear-gradient(135deg, #f8fafc 0%, #ffffff 50%, #f1f5f9 100%)'
    }}>
      {/* Modern Professional Header */}
      <Box sx={{ 
        p: 3,
        background: theme.palette.mode === 'dark' 
          ? 'rgba(15, 23, 42, 0.95)' 
          : 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(20px)',
        borderBottom: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
        boxShadow: theme.palette.mode === 'dark' 
          ? '0 4px 20px rgba(0, 0, 0, 0.3)' 
          : '0 4px 20px rgba(0, 0, 0, 0.1)'
      }}>
      {/* Organization Selector for Root Users */}
      <OrganizationSelector />
      
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h3" sx={{ 
          fontWeight: 700, 
          background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          mb: 1
        }}>
          Welcome back, {user?.firstName}!
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 400 }}>
          Here's what's happening with your projects today
        </Typography>
      </Box>
      </Box>

      {/* Dashboard Content */}
      <Box sx={{ 
        p: 3,
        minHeight: 'calc(100vh - 300px)',
        overflow: 'auto'
      }}>
        {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.light, 0.1)})`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`
            }
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
                    {stats.totalProjects}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Projects
                  </Typography>
                </Box>
                <Avatar sx={{ 
                  bgcolor: alpha(theme.palette.primary.main, 0.2),
                  color: theme.palette.primary.main,
                  width: 56,
                  height: 56
                }}>
                  <DashboardIcon />
                </Avatar>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TrendingUp sx={{ color: 'success.main', fontSize: 20 }} />
                <Typography variant="caption" color="success.main">
                  {stats.activeProjects} active
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.1)}, ${alpha(theme.palette.secondary.light, 0.1)})`,
            border: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`,
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              background: `linear-gradient(90deg, ${theme.palette.secondary.main}, ${theme.palette.info.main})`
            }
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.secondary.main }}>
                    {stats.totalIssues}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Issues
                  </Typography>
                </Box>
                <Avatar sx={{ 
                  bgcolor: alpha(theme.palette.secondary.main, 0.2),
                  color: theme.palette.secondary.main,
                  width: 56,
                  height: 56
                }}>
                  <Assignment />
                </Avatar>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircle sx={{ color: 'success.main', fontSize: 20 }} />
                <Typography variant="caption" color="success.main">
                  {stats.completedIssues} completed
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)}, ${alpha(theme.palette.success.light, 0.1)})`,
            border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              background: `linear-gradient(90deg, ${theme.palette.success.main}, ${theme.palette.warning.main})`
            }
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.success.main }}>
                    {stats.totalUsers}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Team Members
                  </Typography>
                </Box>
                <Avatar sx={{ 
                  bgcolor: alpha(theme.palette.success.main, 0.2),
                  color: theme.palette.success.main,
                  width: 56,
                  height: 56
                }}>
                  <Group />
                </Avatar>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TrendingUp sx={{ color: 'success.main', fontSize: 20 }} />
                <Typography variant="caption" color="success.main">
                  {stats.activeUsers} active
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.1)}, ${alpha(theme.palette.info.light, 0.1)})`,
            border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              background: `linear-gradient(90deg, ${theme.palette.info.main}, ${theme.palette.primary.main})`
            }
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.info.main }}>
                    {Math.round((stats.completedIssues / Math.max(stats.totalIssues, 1)) * 100)}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Completion Rate
                  </Typography>
                </Box>
                <Avatar sx={{ 
                  bgcolor: alpha(theme.palette.info.main, 0.2),
                  color: theme.palette.info.main,
                  width: 56,
                  height: 56
                }}>
                  <Schedule />
                </Avatar>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={(stats.completedIssues / Math.max(stats.totalIssues, 1)) * 100}
                sx={{ 
                  height: 6, 
                  borderRadius: 3,
                  bgcolor: alpha(theme.palette.info.main, 0.2),
                  '& .MuiLinearProgress-bar': {
                    bgcolor: theme.palette.info.main
                  }
                }}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Content Grid */}
      <Grid container spacing={3}>
        {/* Recent Projects */}
        <Grid item xs={12} lg={8}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                  Recent Projects
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<Add />}
                  onClick={() => navigate('/projects')}
                  sx={{ borderRadius: 2 }}
                >
                  View All
                </Button>
              </Box>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {recentProjects.map((project) => (
                  <Paper
                    key={project.id}
                    sx={{
                      p: 2,
                      cursor: 'pointer',
                      transition: 'all 0.2s ease-in-out',
                      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: theme.shadows[8],
                        borderColor: theme.palette.primary.main
                      }
                    }}
                    onClick={() => navigate(`/projects/${project.id}`)}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ 
                          bgcolor: alpha(theme.palette.primary.main, 0.2),
                          color: theme.palette.primary.main
                        }}>
                          {project.name[0]}
                        </Avatar>
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {project.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {project.description || 'No description'}
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip
                          label={project.isActive ? 'Active' : 'Inactive'}
                          color={project.isActive ? 'success' : 'default'}
                          size="small"
                          sx={{ fontWeight: 500 }}
                        />
                        <IconButton size="small">
                          <MoreVert />
                        </IconButton>
                      </Box>
                    </Box>
                  </Paper>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Issues & Top Users */}
        <Grid item xs={12} lg={4}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Recent Issues */}
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  Recent Issues
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {recentIssues.map((issue) => (
                    <Box
                      key={issue.id}
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.background.paper, 0.5),
                        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                        cursor: 'pointer',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          bgcolor: alpha(theme.palette.primary.main, 0.05),
                          borderColor: theme.palette.primary.main
                        }
                      }}
                      onClick={() => navigate(`/issues/${issue.id}`)}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        {getIssueTypeIcon(issue.type)}
                        <Typography variant="body2" sx={{ fontWeight: 500, flex: 1 }}>
                          {issue.title}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip
                          label={issue.priority}
                          color={getPriorityColor(issue.priority) as any}
                          size="small"
                          sx={{ height: 20 }}
                        />
                        <Chip
                          label={issue.status}
                          color={getStatusColor(issue.status) as any}
                          size="small"
                          sx={{ height: 20 }}
                        />
                      </Box>
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>

            {/* Velocity Dashboard (for managers and admins) */}
            {!isEmployee && recentProjects.length > 0 && (
              <VelocityDashboard 
                projectId={recentProjects[0].id} 
                height={300}
                showControls={false}
              />
            )}

            {/* Top Users (hidden for employees) */}
            {!isEmployee && (
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                    Top Contributors
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {topUsers.map((user, index) => (
                      <Box
                        key={user.id}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          p: 1,
                          borderRadius: 2,
                          bgcolor: alpha(theme.palette.background.paper, 0.5),
                          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                        }}
                      >
                        <Box sx={{ position: 'relative' }}>
                          <Avatar
                            src={user.avatar}
                            sx={{ width: 40, height: 40 }}
                          >
                            {user.firstName?.[0]}{user.lastName?.[0]}
                          </Avatar>
                          {index < 3 && (
                            <Box
                              sx={{
                                position: 'absolute',
                                top: -2,
                                right: -2,
                                width: 16,
                                height: 16,
                                borderRadius: '50%',
                                bgcolor: index === 0 ? '#ffd700' : index === 1 ? '#c0c0c0' : '#cd7f32',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '0.6rem',
                                fontWeight: 'bold',
                                color: 'white'
                              }}
                            >
                              {index + 1}
                            </Box>
                          )}
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {user.firstName} {user.lastName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {user.title || 'Team Member'}
                          </Typography>
                        </Box>
                        <Chip
                          label={user.role}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </Box>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            )}
          </Box>
        </Grid>
      </Grid>
      </Box>
    </Box>
  );
};

export default Dashboard;